import { useState, useEffect, useMemo } from 'react';
import { createClient } from '@supabase/supabase-js';
import { TweetTableData } from '../hooks/useAllTweets';

// Use backend's environment variable naming convention (same as useAllTweets)
const supabaseUrl = import.meta.env.VITE_BLOKADE_MAP_SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_BLOKADE_MAP_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;

const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface Hashtag {
  hashtag: string;
  count: number;
}

type SortField = 'created_at' | 'like_count'
type SortOrder = 'asc' | 'desc'

interface CombinedFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortField: SortField;
  sortOrder: SortOrder;
  onSortChange: (field: SortField, order: SortOrder) => void;
  showRecentOnly: boolean;
  onRecentOnlyChange: (show: boolean) => void;
  showTenMinOnly: boolean;
  onTenMinOnlyChange: (show: boolean) => void;
  selectedHashtags: Set<string>;
  onHashtagChange: (hashtag: string) => void;
  allTweets: TweetTableData[];
  filteredCount: number;
  totalCount: number;
}

export function CombinedFilters({
  searchQuery,
  onSearchChange,
  sortField,
  sortOrder,
  onSortChange,
  showRecentOnly,
  onRecentOnlyChange,
  showTenMinOnly,
  onTenMinOnlyChange,
  selectedHashtags,
  onHashtagChange,
  allTweets,
  filteredCount,
  totalCount
}: CombinedFiltersProps) {
  const [hashtags, setHashtags] = useState<Hashtag[]>([]);
  const [isLoadingHashtags, setIsLoadingHashtags] = useState(true);
  const [showAllHashtags, setShowAllHashtags] = useState(false);

  // Calculate dynamic hashtag counts based on current filters
  const dynamicHashtagCounts = useMemo(() => {
    // Apply all filters except hashtag filter
    let filteredForCounts = allTweets;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filteredForCounts = filteredForCounts.filter(tweet => 
        tweet.text.toLowerCase().includes(query) ||
        tweet.author_username.toLowerCase().includes(query) ||
        tweet.author_name.toLowerCase().includes(query)
      );
    }
    
    // Apply time filter - 10 min takes precedence over 30 min
    if (showTenMinOnly) {
      const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000);
      filteredForCounts = filteredForCounts.filter(tweet => 
        new Date(tweet.created_at) > tenMinutesAgo
      );
    } else if (showRecentOnly) {
      const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
      filteredForCounts = filteredForCounts.filter(tweet => 
        new Date(tweet.created_at) > thirtyMinutesAgo
      );
    }

    // Count hashtags considering currently selected hashtags
    const hashtagCounts: Record<string, number> = {};
    
    if (selectedHashtags.size === 0) {
      // When no hashtags are selected, show counts for all hashtags
      filteredForCounts.forEach(tweet => {
        if (tweet.hashtags && tweet.hashtags.length > 0) {
          tweet.hashtags.forEach(hashtag => {
            hashtagCounts[hashtag] = (hashtagCounts[hashtag] || 0) + 1;
          });
        }
      });
    } else {
      // When hashtags are selected, calculate dynamic counts
      filteredForCounts.forEach(tweet => {
        if (tweet.hashtags && tweet.hashtags.length > 0) {
          const tweetHashtagsSet = new Set(tweet.hashtags);
          
          // For each hashtag in this tweet
          tweet.hashtags.forEach(hashtag => {
            if (selectedHashtags.has(hashtag)) {
              // For selected hashtags, count tweets that have ALL selected hashtags
              const hasAllSelected = Array.from(selectedHashtags).every(selectedTag => 
                tweetHashtagsSet.has(selectedTag)
              );
              if (hasAllSelected) {
                hashtagCounts[hashtag] = (hashtagCounts[hashtag] || 0) + 1;
              }
            } else {
              // For unselected hashtags, count tweets that have ALL selected hashtags AND this hashtag
              const hasAllSelectedPlusThis = Array.from(selectedHashtags).every(selectedTag => 
                tweetHashtagsSet.has(selectedTag)
              ) && tweetHashtagsSet.has(hashtag);
              
              if (hasAllSelectedPlusThis) {
                hashtagCounts[hashtag] = (hashtagCounts[hashtag] || 0) + 1;
              }
            }
          });
        }
      });
    }

    // Convert to array and sort by count
    return Object.entries(hashtagCounts)
      .map(([hashtag, count]) => ({ hashtag, count }))
      .sort((a, b) => b.count - a.count);
  }, [allTweets, searchQuery, showTenMinOnly, showRecentOnly, selectedHashtags]);

  useEffect(() => {
    async function fetchHashtags() {
      setIsLoadingHashtags(true);
      try {
        const { data, error } = await supabase.functions.invoke('get-hashtag-counts');
        
        if (error) {
          console.error('❌ Edge function error:', error);
          throw error;
        }
        
        if (data) {
          setHashtags(data);
        } else {
          setHashtags([]);
        }
      } catch (error) {
        console.error('❌ Error fetching hashtags:', error);
        setHashtags([]);
      } finally {
        setIsLoadingHashtags(false);
      }
    }
    fetchHashtags();
  }, []);

  // Handle sort toggle
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      onSortChange(field, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSortChange(field, 'desc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  // Use dynamic counts if available, otherwise fall back to static counts
  const displayHashtags = isLoadingHashtags ? [] : 
    (dynamicHashtagCounts.length > 0 ? dynamicHashtagCounts : hashtags);
  const visibleHashtags = showAllHashtags ? displayHashtags : displayHashtags.slice(0, 8);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-4 md:mb-6 overflow-hidden">
      <div className="p-3 md:p-4 space-y-3">
        {/* Search and main controls - all inline on desktop */}
        <div className="flex flex-col lg:flex-row gap-2 lg:gap-3 lg:items-center">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Pretraži tvitove, korisnike..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
            />
          </div>
          
          <div className="flex gap-1.5 flex-wrap lg:flex-nowrap">
            <button
              onClick={() => handleSort('created_at')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                sortField === 'created_at' 
                  ? 'bg-blue-500 text-white shadow-md' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Skorije {getSortIcon('created_at')}
            </button>
            <button
              onClick={() => handleSort('like_count')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                sortField === 'like_count' 
                  ? 'bg-blue-500 text-white shadow-md' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Popularno {getSortIcon('like_count')}
            </button>
            <button
              onClick={() => {
                onTenMinOnlyChange(!showTenMinOnly);
                if (!showTenMinOnly) {
                  onRecentOnlyChange(false); // Turn off 30min when turning on 10min
                }
              }}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                showTenMinOnly 
                  ? 'bg-blue-500 text-white shadow-md' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              &lt;10min
            </button>
            <button
              onClick={() => {
                onRecentOnlyChange(!showRecentOnly);
                if (!showRecentOnly) {
                  onTenMinOnlyChange(false); // Turn off 10min when turning on 30min
                }
              }}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                showRecentOnly 
                  ? 'bg-blue-500 text-white shadow-md' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              &lt;30min
            </button>
            <span className="hidden lg:flex items-center text-sm text-gray-600 ml-2">
              <span className="font-medium text-blue-600">{filteredCount}</span>
              <span className="mx-1">od</span>
              <span className="font-medium">{totalCount}</span>
            </span>
          </div>
        </div>

        {/* Hashtag filters - more compact */}
        <div className="flex flex-wrap gap-1.5">
          {isLoadingHashtags ? (
            <div className="flex items-center py-1">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              <span className="ml-2 text-sm text-gray-500">Učitavanje...</span>
            </div>
          ) : displayHashtags.length > 0 && (
            <>
              {visibleHashtags.map(({ hashtag, count }) => (
                <button
                  key={hashtag}
                  onClick={() => onHashtagChange(hashtag)}
                  className={`px-2.5 py-1 text-sm font-medium rounded-full transition-all duration-200 ${
                    selectedHashtags.has(hashtag)
                      ? 'bg-blue-500 text-white shadow-md scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-105'
                  }`}
                >
                  #{hashtag} 
                  <span className={`ml-0.5 text-xs ${
                    selectedHashtags.has(hashtag) ? 'text-blue-200' : 'text-gray-500'
                  }`}>
                    ({count})
                  </span>
                </button>
              ))}
              {displayHashtags.length > 8 && (
                <button
                  onClick={() => setShowAllHashtags(!showAllHashtags)}
                  className="px-2.5 py-1 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                >
                  {showAllHashtags ? 'Manje' : `+${displayHashtags.length - 8}`}
                </button>
              )}
            </>
          )}
        </div>

        {/* Clear filters - compact */}
        {(searchQuery || showRecentOnly || showTenMinOnly || selectedHashtags.size > 0) && (
          <button
            onClick={() => {
              onSearchChange('');
              onRecentOnlyChange(false);
              onTenMinOnlyChange(false);
              selectedHashtags.forEach((hashtag: string) => onHashtagChange(hashtag));
            }}
            className="text-sm text-red-600 hover:text-red-800 font-medium transition-colors"
          >
            Očisti sve
          </button>
        )}
      </div>
    </div>
  );
} 